/* eslint-disable camelcase */
import type { GenericAbortSignal } from 'axios';
import qs from 'query-string';
import { newCancelTokenFn } from 'hooks/useCancelToken';
import { doorClient, ImResponse, resolve } from '.';

/**
 * 订单查询 V2 接口类型。
 * 对应后端 TypeSpec: TradeV2 / GET /aggregation/v2/trade/search
 * 数据模型对应 leyan-proto 1.45.6: overseaim_trade_v2.proto
 */

/** proto int64 在 JSON 中可能表现为 number 或 string */
export type Long = number | string;

export type ExternalType =
  | 'RESERVED'
  | 'SHOPEE'
  | 'LAZADA'
  | 'TOKOPEDIA'
  | 'TIKTOK'
  | 'ALIEXPRESS'
  | 'MIXED'
  | 'TIKTOK_US'
  | 'MERCADO'
  | 'KWAI'
  | 'FACEBOOK'
  | 'JOLECT';

export type LeyanTradeStatus =
  | 'UNKNOWN_ORDER_STATUS'
  | 'WAITING_PAY'
  | 'WAITING_DELIVERY'
  | 'WAITING_SIGN'
  | 'TRADE_SUCCESS'
  | 'CANCEL'
  | 'REFUND'
  | 'MULTIPLE_STATUS'
  | 'IN_CANCELING';

export type LeChatTradeStatus =
  | 'LE_CHAT_UNKNOWN_TRADE'
  | 'LE_CHAT_NO_ORDER'
  | 'LE_CHAT_UNPAID'
  | 'LE_CHAT_READY_TO_SHIP'
  | 'LE_CHAT_IN_CANCEL'
  | 'LE_CHAT_WAIT_CONFIRM'
  | 'LE_CHAT_LOST'
  | 'LE_CHAT_DAMAGED'
  | 'LE_CHAT_CANCELED'
  | 'LE_CHAT_TO_RETURN'
  | 'LE_CHAT_RETURNED'
  | 'LE_CHAT_IN_RETURN'
  | 'LE_CHAT_RETURN_SUCCESS'
  | 'LE_CHAT_RETURN_FAILED'
  | 'LE_CHAT_RETURN_SCRAPPED'
  | 'LE_CHAT_COMPLETED';

export type LeChatAfterSalesStatus =
  | 'LE_CHAT_AFTER_SALES_UNKNOWN'
  | 'LE_CHAT_AFTER_SALES_REQUEST_PENDING'
  | 'LE_CHAT_AFTER_SALES_RETURNING'
  | 'LE_CHAT_AFTER_SALES_COMPLETED'
  | 'LE_CHAT_AFTER_SALES_CLOSED';

export type CancelBy = 'EMPTY_ROLE' | 'BUYER' | 'SELLER' | 'SYSTEM' | 'UNKNOWN_ROLE';

export type CancelReason =
  | 'EMPTY_REASON'
  | 'ORDER_PAYMENT_TIMEOUT'
  | 'DELIVERY_FAILED'
  | 'SELLER_SHIPPING_DELAY'
  | 'PACKAGE_LOST'
  | 'PACKAGE_DAMAGED'
  | 'RECIPIENT_UNREACHABLE'
  | 'INVALID_DELIVERY_ADDRESS'
  | 'OTHERS';

export type DeliveryMethodType =
  | 'UNKNOWN_METHOD'
  | 'LOCAL_SELLER'
  | 'SHOPEE'
  | 'CROSS_BORDER_SELLER';

export type TradeV2Section =
  | 'SECTION_UNSPECIFIED'
  | 'ALL'
  | 'ITEMS'
  | 'PACKAGES'
  | 'REVERSE'
  | 'PAYMENT'
  | 'RECEIVER'
  | 'REMARK'
  | 'STATUS_TIMELINE'
  | 'ERP'
  | 'COMMENTS';

export type TagType =
  | 'TYPE_UNSPECIFIED'
  | 'PRE_ORDER'
  | 'COD'
  | 'AFFECTS_REPUTATION'
  | 'MATCH_BOOKING'
  | 'FROM_EXCHANGE'
  | 'FROM_REPLACEMENT'
  | 'GENERATED_EXCHANGE'
  | 'GENERATED_REPLACEMENT'
  | 'ITEM_PRE_ORDER'
  | 'ITEM_FREE_GIFT'
  | 'ITEM_REFUNDED'
  | 'ITEM_SEARCH_HIT'
  | 'ITEM_COMBINED_LISTING'
  | 'REVERSE_IN_MEDIATION'
  | 'REVERSE_IN_CLAIM';

export type ReturnType =
  | 'TYPE_UNSPECIFIED'
  | 'RETURN_REFUND'
  | 'ONLY_REFUND'
  | 'EXCHANGE'
  | 'REPLACEMENT';

export type Initiator = 'INITIATOR_UNSPECIFIED' | 'BUYER' | 'SELLER' | 'PLATFORM';

export type StatusInfoTagId =
  | 'TAG_ID_UNSPECIFIED'
  | 'WILL_CANCEL_SOON'
  | 'SHIP_BEFORE_DEADLINE'
  | 'SHIP_OVERDUE'
  | 'SHIP_WITHIN_HOUR'
  | 'WILL_CANCEL_AT_TIME';

export type ErpType = 'TYPE_UNSPECIFIED' | 'HISENSE' | 'QIANYI' | 'JUSHUITAN' | 'YOUPIN' | 'OTHER';

export interface PreDispute {
  failReasonCode?: number;
  content?: string;
}

export interface Video {
  thumbnailUrl?: string;
  videoUrl?: string;
}

export interface PageInfoV2 {
  pageSize?: number;
  pageNo?: number;
  hasNext?: boolean;
  totalCount?: number;
}

export interface ExternalBuyerV2 {
  platform?: ExternalType;
  region?: string;
  externalId?: string;
}

export interface Tag {
  type?: TagType;
  refId?: string;
}

export interface Operator {
  userId?: Long;
  userName?: string;
  operateTimeMs?: Long;
}

export interface TradeBase {
  internalTradeId?: Long;
  externalTradeId?: string;
  platform?: ExternalType;
  region?: string;
  internalStoreId?: Long;
  externalStoreId?: string;
  storeName?: string;
  storeAlias?: string;
  storeType?: string;
  internalBuyerId?: Long;
  externalBuyerId?: string;
  buyerNick?: string;
  lechatStatuses?: LeChatTradeStatus[];
  statusesOriginal?: string[];
  sidebarStatus?: LeyanTradeStatus;
  createTimeMs?: Long;
  updateTimeMs?: Long;
  payTimeMs?: Long;
  shipDeadlineTimeMs?: Long;
  pickupDoneTimeMs?: Long;
  completeTimeMs?: Long;
  currency?: string;
  currencySymbol?: string;
  totalAmount?: string;
  fulfilledBy?: DeliveryMethodType;
  fulfilledByOriginal?: string;
  shippingMode?: string;
  shippingCarrier?: string;
  paymentMethod?: string;
  tags?: Tag[];
  invoiceNumber?: string;
}

export interface ItemSection {
  items?: Item[];
}

export interface Item {
  orderLineIds?: string[];
  externalItemId?: string;
  externalSkuId?: string;
  /** 主商品货号 */
  itemSku?: string;
  sellerSku?: string;
  shopSku?: string;
  itemName?: string;
  skuName?: string;
  itemPic?: string;
  itemUrl?: string;
  quantity?: number;
  unitPrice?: string;
  originPrice?: string;
  discountedPrice?: string;
  externalReturnIds?: string[];
  externalCancelIds?: string[];
  subItems?: SubItem[];
  promotionType?: string;
  promotionId?: string;
  promotionRole?: string;
  tags?: Tag[];
  lechatStatus?: LeChatTradeStatus;
  statusesOriginal?: string[];
  sidebarStatus?: LeyanTradeStatus;
  warehouse?: string;
  prepareTimeDays?: number;
  digitalInfo?: string;
  weight?: string;
}

export interface SubItem {
  orderLineId?: string;
  subOrderLineId?: string;
  externalItemId?: string;
  externalSkuId?: string;
  sellerSku?: string;
  itemName?: string;
  skuName?: string;
  itemPic?: string;
  itemUrl?: string;
  quantity?: number;
  packageId?: string;
}

export interface PackageSection {
  packages?: Package[];
}

export interface Package {
  packageId?: string;
  orderLineIds?: string[];
  subOrderLineIds?: string[];
  createTimeMs?: Long;
  shipDeadlineTimeMs?: Long;
  trackingNumber?: string;
  shipmentId?: string;
  isShipmentArranged?: boolean;
  statusInfoTag?: StatusInfoTag;
  serviceTypeIdentifier?: string;
}

export interface StatusInfoTag {
  tagId?: StatusInfoTagId;
  timestampMs?: Long;
}

export interface ReverseSection {
  cancels?: Cancel[];
  returns?: Return[];
}

export interface Cancel {
  externalCancelId?: string;
  internalCancelId?: Long;
  cancelStatusOriginal?: string;
  handleStatus?: string;
  createTimeMs?: Long;
  updateTimeMs?: Long;
  handleDeadlineTimeMs?: Long;
  cancelLines?: CancelLine[];
  orderLineIds?: string[];
  subOrderLineIds?: string[];
  cancelBy?: CancelBy;
  cancelByOriginal?: string;
  cancelReason?: CancelReason;
  cancelReasonOriginal?: string;
  availableOperations?: string[];
  operationLogs?: ReverseOperationLog[];
}

export interface CancelLine {
  orderLineId?: string;
  subOrderLineIds?: string[];
  externalItemId?: string;
  externalSkuId?: string;
  sellerSku?: string;
  itemName?: string;
  skuName?: string;
  itemPic?: string;
  quantity?: number;
}

export interface Return {
  externalReturnId?: string;
  internalReturnId?: Long;
  returnType?: ReturnType;
  returnStatusOriginal?: string;
  status?: LeChatAfterSalesStatus;
  displayStatus?: string;
  handleStatus?: string;
  createTimeMs?: Long;
  updateTimeMs?: Long;
  handleDeadlineTimeMs?: Long;
  returnLines?: ReturnLine[];
  orderLineIds?: string[];
  subOrderLineIds?: string[];
  refundAmount?: string;
  partiallyRefundAmount?: string;
  reasonCode?: string;
  reasonText?: string;
  reassessedReasonCode?: string;
  buyerImages?: string[];
  buyerVideos?: Video[];
  initiatedBy?: Initiator;
  offerCounterLimit?: number;
  allowOffers?: AllowOfferV2[];
  latestOfferAmount?: string;
  offerAutoAcceptTimeMs?: Long;
  negotiationStatus?: string;
  sellerProofStatus?: string;
  sellerEvidenceDeadlineTimeMs?: Long;
  sellerCompensationStatus?: string;
  sellerCompensationDeadlineTimeMs?: Long;
  compensationAmount?: string;
  returnTrackingNumber?: string;
  returnShipmentId?: string;
  returnShipDeadlineTimeMs?: Long;
  returnSellerDeadlineTimeMs?: Long;
  preDispute?: PreDispute;
  histories?: ReturnHistoryV2[];
  availableOperations?: string[];
  operationLogs?: ReverseOperationLog[];
  tags?: Tag[];
  reasonName: string;
}

export interface ReturnLine {
  externalReturnLineId?: string;
  orderLineId?: string;
  subOrderLineIds?: string[];
  externalItemId?: string;
  externalSkuId?: string;
  sellerSku?: string;
  itemName?: string;
  skuName?: string;
  itemPic?: string;
  quantity?: number;
  refundAmount?: string;
  returnStatusOriginal?: string;
  handleDeadlineTimeMs?: Long;
  availableOperations?: string[];
  histories?: ReturnHistoryV2[];
}

export interface ReturnHistoryV2 {
  statusOriginal?: string;
  updateTimeMs?: Long;
}

export interface AllowOfferV2 {
  returnType?: ReturnType;
  maxRefundAmount?: string;
  refundAmountAdjustable?: boolean;
  latestOfferAmount?: string;
}

export interface ReverseOperationLog {
  operator?: Operator;
  operate?: string;
  raw?: string;
}

export interface PaymentSection {
  buyerPaid?: BuyerPaid;
  sellerIncome?: SellerIncome;
}

export interface BuyerPaid {
  currency?: string;
  currencySymbol?: string;
  itemSubtotal?: ItemSubtotal;
  shippingFee?: ShippingFee;
  taxAmount?: string;
  importTax?: string;
  icmsTax?: string;
  otherTaxes?: string;
  minimumSpendSurcharge?: string;
  serviceFee?: string;
  buyerTransactionFee?: string;
  productProtectionFee?: string;
  coinsDiscount?: string;
  creditCardDiscount?: string;
  buyerPaidTotal?: string;
}

export interface ItemSubtotal {
  total?: string;
  originalAmount?: string;
  sellerDiscount?: string;
  platformDiscount?: string;
}

export interface ShippingFee {
  total?: string;
  originalAmount?: string;
  sellerDiscount?: string;
  platformDiscount?: string;
  totalDiscount?: string;
  salesServiceTax?: string;
}

export interface SellerIncome {
  currency?: string;
  currencySymbol?: string;
  itemAmount?: ItemAmount;
  shippingAmount?: ShippingAmount;
  discountAmount?: DiscountAmount;
  feesAndCharges?: FeesAndCharges;
  receivableAmount?: string;
  adjustments?: Adjustment[];
  totalAdjustment?: string;
  settledAmount?: string;
  customItems?: CustomIncomeItem[];
}

export interface Adjustment {
  reason?: string;
  amount?: string;
  adjustedTimeMs?: Long;
}

export interface CustomIncomeItem {
  name?: string;
  amount?: string;
}

export interface ItemAmount {
  total?: string;
  originalAmount?: string;
  refundedAmount?: string;
  salesAmount?: string;
  cancelledAmount?: string;
}

export interface ShippingAmount {
  total?: string;
  buyerPaid?: string;
  carrierCharged?: string;
  refunded?: string;
  platformRebate?: string;
  carrierDiscount?: string;
}

export interface DiscountAmount {
  total?: string;
  sellerVoucher?: string;
  platformSubsidy?: string;
}

export interface FeesAndCharges {
  total?: string;
  commission?: string;
  commissionRefunded?: string;
  serviceFee?: string;
  transactionFee?: string;
  orderProcessingFee?: string;
  vat?: string;
  importDuty?: string;
  lowValueGoodsSalesTax?: string;
}

export interface ReceiverSection {
  name?: string;
  phone?: string;
  mobile?: string;
  email?: string;
  country?: string;
  state?: string;
  city?: string;
  district?: string;
  town?: string;
  address?: string;
  postCode?: string;
  taxIdNumber?: string;
  taxIdType?: string;
}

export interface RemarkSection {
  buyer?: Remark;
  platform?: Remark;
  lechat?: Remark;
  logs?: RemarkLog[];
}

export interface Remark {
  content?: string;
  display?: boolean;
  editable?: boolean;
}

export interface RemarkLog {
  operator?: Operator;
  before?: string;
  after?: string;
}

export interface TimelineSection {
  tradeStatusTimeline?: TradeStatusTimelineNodeV2[];
}

export interface TradeStatusTimelineNodeV2 {
  status?: LeChatTradeStatus;
  statusOriginal?: string;
  statusTimeMs?: Long;
  syncedTimeMs?: Long;
}

export interface ErpSection {
  erpType?: ErpType;
  erpTypeOriginal?: string;
  erpTradeStatus?: string;
  processStatus?: string;
  preWeight?: string;
  realWeight?: string;
  logisticsCompanyPriceWeight?: string;
  oaNoteContent?: string;
  erpOrderTags?: string;
  exchangeRecords?: ExchangeRecord[];
  availableExchanges?: ExchangeOption[];
}

export interface ExchangeRecord {
  exchangedSku?: string;
  exchangedQuantity?: number;
}

export interface ExchangeOption {
  exchangedSku?: string;
  exchangedQuantity?: number;
  commodityCost?: string;
  prodUnitCost?: string;
  onSale?: boolean;
  outOfStock?: boolean;
}

export interface CommentSection {
  comments?: ItemComment[];
}

export interface ItemComment {
  commentId?: string;
  orderLineIds?: string[];
  externalItemId?: string;
  externalSkuId?: string;
  buyerComment?: string;
  imageUrls?: string[];
  videoUrls?: string[];
  rating?: CommentRating;
  updateTimeMs?: Long;
}

export interface CommentRating {
  ratingStar?: number;
  sellerRating?: number;
  productRating?: number;
  logisticsRating?: number;
}

export interface TradeV2Order {
  base?: TradeBase;
  items?: ItemSection;
  packages?: PackageSection;
  reverse?: ReverseSection;
  payment?: PaymentSection;
  receiver?: ReceiverSection;
  remark?: RemarkSection;
  timeline?: TimelineSection;
  erp?: ErpSection;
  comments?: CommentSection;
}

export interface SearchTradesV2Data {
  page?: PageInfoV2;
  trades?: TradeV2Order[];
  includedSections?: TradeV2Section[];
}

export interface SearchTradesV2Query {
  internalStoreId: Long;
  pageNo?: number;
  pageSize?: number;
  internalBuyerId?: Long;
  externalBuyerId?: string;
  sidebarStatus?: LeyanTradeStatus;
  internalTradeIds?: string[];
  externalTradeIds?: string[];
  trackingNumber?: string;
  invoiceNumber?: string;
  externalItemId?: string;
  externalSkuId?: string;
  sellerSku?: string;
  itemName?: string;
  refreshImmediately?: boolean;
  filterCanceledReverse?: boolean;
  include?: TradeV2Section[];
  orgId?: string;
}

/**
 * 订单查询 V2。数组参数使用重复 query key（include=ITEMS&include=REVERSE）。
 */
export function tradeListV2(
  params: SearchTradesV2Query,
  newCancelToken?: newCancelTokenFn,
  signal?: GenericAbortSignal,
) {
  return resolve(
    doorClient.get<ImResponse<SearchTradesV2Data>>('/aggregation/v2/trade/search', {
      params,
      paramsSerializer: (query) =>
        qs.stringify(query, {
          arrayFormat: 'none',
          skipNull: true,
          skipEmptyString: true,
        }),
      cancelToken: newCancelToken?.(),
      signal,
    }),
  );
}
